<template>
  <div
    v-if="isLoading"
    class="pt-8"
    style="text-align: center;"
  >
    <ngc-loading-spinner
      size="medium"
      aria-label="Please wait"
    />
    <div class="gator-body-1 mt-4">
      {{ loaderMessage[0] }}
    </div>
  </div>
  <!-- Custom handling for the closing portal (uses custom text from the new auth flow, and adds a back action to one state)-->
  <div v-else-if="(isSigningRoom) && ['auth_sign_in', 'auth_input_email', 'auth_send_verification', 'auth_send_verification_email_only', 'auth_waiting_for_verification', 'new_account', 'auth_complete'].includes(state)">
    <div v-if="state.includes('auth_sign_in')">
      <div
        class="gator-heading-h2 mb-6"
        data-cy="title"
        :style="isMobile ? 'text-align: center' : ''"
      >
        Welcome
      </div>
      <ngc-input-label
        class="mb-2 gator-body-1"
        data-cy="email-label"
      >
        Email
      </ngc-input-label>
      <ngc-input-text
        id="email"
        type="email"
        data-cy="email-field"
        :autofocus="true"
        :value="current_state_definition.fields[0].response || ''"
        :error-text="current_state_definition.fields[0].error || ''"
        @input="current_state_definition.fields[0].response = $event.detail.value"
        @focus="clearErrors(0)"
        @blur="checkErrors(0)"
        @keydown.enter="keydownEnter()"
      />
      <ngc-input-label
        class="mb-2 mt-4 gator-body-1"
        data-cy="password-label"
      >
        Password
      </ngc-input-label>
      <ngc-input-text
        id="password"
        type="password"
        class="mb-4"
        data-cy="password-field"
        :name="current_state_definition.fields[1].hint"
        :disabled="current_state_definition.fields[1].disabled"
        :help-text="current_state_definition.fields[1].requirements"
        :value="current_state_definition.fields[1].response || ''"
        :error-text="current_state_definition.fields[1].error || ''"
        @input="current_state_definition.fields[1].response = $event.detail.value"
        @focus="clearErrors(1)"
        @blur="checkErrors(1)"
        @keydown.enter="keydownEnter()"
      />
      <ngc-alert
        v-if="error"
        variant="error"
        data-cy="login-error-message"
      >
        {{ errorMessages ? errorMessages[0] : '' }}
      </ngc-alert>
      <div class="forgot-password-row">
        <ngc-button
          variant="base"
          class="forgot-password-btn"
          data-cy="forgot-password-link"
          size="medium"
          @click="nextState(current_state_definition.secondary_action);"
        >
          Forgot your password?
        </ngc-button>
      </div>
      <div :style="cssSignInButtonRow">
        <ngc-button
          variant="primary"
          class="signin-btn"
          aria-label="sign in"
          data-cy="sign-in-btn"
          size="medium"
          :loading="waitingRequests['sign_in']"
          :disabled="waitingRequest && !waitingRequests['sign_in']"
          @click="nextState(current_state_definition.primary_action);"
        >
          Sign in
        </ngc-button>
      </div>
    </div>
    <div v-else-if="state.includes('auth_input_email')">
      <div
        class="gator-heading-h2 mb-6"
        data-cy="recover-password-title"
        :style="isMobile ? 'text-align: center' : ''"
      >
        Recover password
      </div>
      <div
        class="mb-6 gator-body-1"
        data-cy="recover-password-text"
      >
        It's okay, it happens to all of us. Let us help you recover your password.
      </div>
      <ngc-input-label
        class="mb-2 gator-body-1"
        data-cy="recover-email-label"
      >
        What is your email?
      </ngc-input-label>
      <ngc-input-text
        id="email"
        class="mb-8"
        type="email"
        data-cy="recover-email-field"
        :autofocus="true"
        :value="current_state_definition.fields[0].response || ''"
        :error-text="current_state_definition.fields[0].error || ''"
        @input="current_state_definition.fields[0].response = $event.detail.value"
        @focus="clearErrors(0)"
        @blur="checkErrors(0)"
        @keydown.enter="keydownEnter()"
      />
      <ngc-alert
        v-if="error"
        variant="error"
        data-cy="login-error-message"
      >
        {{ errorMessages ? errorMessages[0] : '' }}
      </ngc-alert>
      <div :style="cssButtonRow">
        <ngc-button
          variant="primary"
          :class="isMobile ? 'full-width-btn' : 'fixed-width-btn'"
          aria-label="continue"
          data-cy="continue"
          size="large"
          :loading="waitingRequests['connect']"
          :disabled="waitingRequest && !waitingRequests['connect']"
          @click="nextState(current_state_definition.primary_action)"
        >
          Continue
        </ngc-button>
        <ngc-button
          data-cy="back"
          size="large"
          :variant="isMobile ? 'secondary' : 'base'"
          :class="isMobile ? 'full-width-btn' : ''"
          :style="isMobile ? 'margin-top: 10px;' : ''"
          :start-icon="isMobile ? '' : 'arrow-left'"
          @click="nextState(current_state_definition.secondary_action);"
        >
          Back
        </ngc-button>
      </div>
    </div>
    <div v-else-if="state.includes('auth_send_verification') || state.includes('auth_send_verification_email_only')">
      <div
        class="gator-heading-h2 mb-6"
        data-cy="send-code-message"
        :style="isMobile ? 'text-align: center' : ''"
      >
        For full access, where would you like us to send your verification code?
      </div>
      <ngc-radio-group
        v-if="current_state_definition.fields[0].options"
        :value.prop="current_state_definition.fields[0].response"
        required
        data-cy="radio-option"
        class="mt-2"
        @change="current_state_definition.fields[0].response = $event.detail"
      >
        <ngc-radio
          v-for="(option, index) in filteredOptions(current_state_definition.fields[0].options)"
          :key="`option-${index}`"
          :value="option.value"
          :title-text="option.label"
        />
      </ngc-radio-group>
      <ngc-alert
        v-if="error"
        variant="error"
        data-cy="login-error-message"
      >
        {{ errorMessages ? errorMessages[0] : '' }}
      </ngc-alert>
      <div :style="cssButtonRow + 'margin-top: 32px'">
        <ngc-button
          variant="primary"
          :class="isMobile ? 'full-width-btn' : 'fixed-width-btn'"
          aria-label="continue"
          data-cy="continue"
          size="large"
          :loading="waitingRequests['send_code']"
          :disabled="waitingRequest && !waitingRequests['send_code']"
          @click="nextState(current_state_definition.primary_action)"
        >
          Continue
        </ngc-button>
        <ngc-button
          data-cy="back"
          size="large"
          :variant="isMobile ? 'secondary' : 'base'"
          :class="isMobile ? 'full-width-btn' : ''"
          :style="isMobile ? 'margin-top: 10px;' : ''"
          :start-icon="isMobile ? '' : 'arrow-left'"
          @click="(() => this.$router.go())"
        >
          Back
        </ngc-button>
      </div>
    </div>
    <div v-else-if="state.includes('auth_waiting_for_verification')">
      <div
        class="gator-heading-h2 mb-6"
        data-cy="code-sent-message"
        :style="isMobile ? 'text-align: center' : ''"
      >
        Your code was sent.<br>
        Please enter it below:
      </div>
      <ngc-input-label
        class="mb-2 gator-body-1"
        data-cy="code-label"
      >
        6 digit verification code
      </ngc-input-label>
      <ngc-input-text
        id="code"
        data-cy="code-field"
        :autofocus="true"
        :value="current_state_definition.fields[0].response || ''"
        :error-text="current_state_definition.fields[0].error || ''"
        @input="current_state_definition.fields[0].response = $event.detail.value"
        @focus="clearErrors(0)"
        @blur="checkErrors(0)"
        @keydown.enter="keydownEnter()"
      />
      <ngc-checkbox
        class="mt-2"
        :selected.prop="current_state_definition.fields[1].response"
        :title-text="current_state_definition.fields[1].label"
        @change="current_state_definition.fields[1].response = $event.detail.selected"
      />

      <ngc-alert
        v-if="error"
        variant="error"
        data-cy="code-error-message"
      >
        {{ errorMessages ? errorMessages[0] : '' }}
      </ngc-alert>
      <div :style="cssButtonRow + 'margin-top: 32px'">
        <ngc-button
          variant="primary"
          :class="isMobile ? 'full-width-btn' : 'fixed-width-btn'"
          aria-label="verify"
          data-cy="verify-code-btn"
          size="large"
          :loading="waitingRequests['verify']"
          :disabled="waitingRequest && !waitingRequests['verify']"
          @click="nextState(current_state_definition.primary_action);"
        >
          Verify code
        </ngc-button>
        <ngc-button
          data-cy="back"
          size="large"
          :variant="isMobile ? 'secondary' : 'base'"
          :class="isMobile ? 'full-width-btn' : ''"
          :style="isMobile ? 'margin-top: 10px;' : ''"
          :start-icon="isMobile ? '' : 'arrow-left'"
          @click="nextState(current_state_definition.secondary_action);"
        >
          Back
        </ngc-button>
      </div>
    </div>
    <div v-else-if="state.includes('new_account')">
      <div
        class="gator-heading-h2 mb-6"
        data-cy="contact-lender-message"
        :style="isMobile ? 'text-align: center' : ''"
      >
        Please contact your lender to verify your email address.
      </div>
      <ngc-alert
        v-if="error"
        variant="error"
        data-cy="email-error-message"
      >
        {{ errorMessages ? errorMessages[0] : '' }}
      </ngc-alert>
      <div :style="cssButtonRow + 'margin-top: 32px'">
        <ngc-button
          variant="primary"
          :class="isMobile ? 'full-width-btn' : 'fixed-width-btn'"
          aria-label="start"
          data-cy="start-over-btn"
          size="large"
          :loading="waitingRequests['start']"
          :disabled="waitingRequest && !waitingRequests['start']"
          @click="nextState(current_state_definition.primary_action);"
        >
          Start over
        </ngc-button>
      </div>
    </div>
    <div v-else-if="state.includes('auth_complete')">
      <div
        class="gator-heading-h2 mb-6"
        :style="isMobile ? 'text-align: center' : ''"
      >
        You're all set!
      </div>

      <div :style="cssButtonRow + 'margin-top: 32px'">
        <ngc-button
          variant="primary"
          :class="isMobile ? 'full-width-btn' : 'fixed-width-btn'"
          aria-label="complete"
          data-cy="complete"
          size="large"
          :loading="waitingRequests['complete']"
          :disabled="waitingRequest && !waitingRequests['complete']"
          @click="nextState(current_state_definition.primary_action);"
        >
          Continue
        </ngc-button>
        <ngc-button
          data-cy="password-reset"
          size="large"
          :variant="isMobile ? 'secondary' : 'base'"
          :class="isMobile ? 'full-width-btn' : ''"
          :style="isMobile ? 'margin-top: 10px;' : ''"
          @click="nextState(current_state_definition.additional_actions[0]);"
        >
          Reset password
        </ngc-button>
      </div>
    </div>
  </div>
  <!-- This block handles all other state combinations -->
  <div v-else>
    <div
      v-if="current_state_definition.title"
      class="gator-heading-h2 mb-6"
      :style="isMobile ? 'text-align: center' : ''"
    >
      {{ current_state_definition.title }}
    </div>
    <div
      v-if="current_state_definition.subtitle"
      class="gator-body-1 mb-6"
    >
      {{ current_state_definition.subtitle }}
    </div>
    <div
      v-for="(field,index) in current_state_definition.fields"
      :key="'field-' + index"
    >
      <div
        v-if="field.text"
        id="content"
        v-html="field.text"
      />
      <ngc-radio-group
        v-if="field.options"
        :value.prop="field.response"
        required
        data-cy="lo-list"
        class="mt-2"
        @change="field.response = $event.detail"
      >
        <ngc-radio
          v-for="(option, index) in filteredOptions(field.options)"
          :key="`option-${index}`"
          :data-cy="option.label ? getCypressTag(option.label + '-input') : 'radio-input'"
          :value="option.value"
          :title-text="option.label"
        />
      </ngc-radio-group>
      <div v-if="!field.options && (field.type !== 'phone' && field.type !== 'password' && field.type !== 'checkbox')">
        <ngc-input-label
          class="mb-2 gator-body-1"
          :data-cy="field.type + '-label'"
        >
          {{ field.hint }}
        </ngc-input-label>
        <ngc-input-text
          type="text"
          :autofocus="index === 0"
          :class="'sel_input_' + index"
          :disabled="field.disabled"
          :error-text="field.error || ''"
          :data-cy="field.type + '-field'"
          :name="field.hint"
          :value="field.response || ''"
          @input="field.response = $event.detail.value"
          @focus="clearErrors(index)"
          @blur="checkErrors(index)"
          @keydown.enter="keydownEnter()"
        />
      </div>
      <div v-if="!field.options && field.type === 'phone'">
        <ngc-input-label
          class="mb-2 gator-body-1"
        >
          {{ field.hint }}
        </ngc-input-label>
        <ngc-input-text
          placeholder="(___) ___-____"
          :error-text="field.error || ''"
          :disabled="field.disabled"
          :value="field.response || ''"
          @input="field.response = $event.detail.value"
          @focus="clearErrors(index)"
          @blur="checkErrors(index)"
          @keydown.enter="keydownEnter()"
        />
      </div>
      <div v-if="field.type === 'password'">
        <ngc-input-label
          class="mb-2 mt-4 gator-body-1"
          :data-cy="field.type + '-label'"
        >
          {{ field.hint }}
        </ngc-input-label>
        <ngc-input-text
          :id="field.hint"
          type="password"
          class="mb-4"
          placeholder=""
          :name="field.hint"
          :disabled="field.disabled"
          :help-text="field.requirements || ''"
          :data-cy="field.type + '-field'"
          :value="field.response || ''"
          :error-text="field.error || ''"
          @input="field.response = $event.detail.value"
          @focus="clearErrors(index)"
          @blur="checkErrors(index)"
          @keydown.enter="keydownEnter()"
        />
      </div>
      <ngc-checkbox
        v-if="field.type === 'checkbox'"
        class="mt-2"
        :selected.prop="field.response"
        :title-text="field.label"
        @change="field.response = $event.detail.selected"
      />
    </div>
    <ngc-alert
      v-if="error"
      variant="error"
      data-cy="login-error-message"
    >
      {{ errorMessages ? errorMessages[0] : '' }}
    </ngc-alert>
    <div
      v-if="actions.length"
      :style="cssButtonRow + 'margin-top: 32px'"
    >
      <ngc-button
        v-for="(action, index) in actions.slice()"
        :key="'action-' + index"
        :variant="action === current_state_definition.primary_action ? 'primary' : isMobile ? 'secondary' : 'base'"
        :class="isMobile ? 'full-width-btn' : 'fixed-width-btn'"
        :style="isMobile && action !== current_state_definition.primary_action ? 'margin-top: 10px' : ''"
        :aria-label="formatActionLabel(action.label)"
        :data-cy="getCypressTag(action.value)"
        size="large"
        :loading="waitingRequests[action.value]"
        :disabled="waitingRequest && !waitingRequests[action.value]"
        @click="nextState(action);"
      >
        {{ formatActionLabel(action.label) }}
      </ngc-button>
    </div>
  </div>
</template>

<script>
import snValidationsService from '~/services/snValidationsService.js'
import cookie from 'js-cookie'

export default {

  name: 'StatefulAuth',

  props: {
    activationCode: {
      type: String,
      default: '',
      required: false
    },
    autoLogin: {
      type: Boolean,
      default: false,
      required: false
    },
    userEmail: {
      type: String,
      default: '',
      required: false
    },
    init: {
      type: String,
      default: '',
      required: false
    },
    supressRedirects: {
      type: Boolean,
      default: false,
      required: false
    },
    authContext: {
      type: String,
      default: '',
      required: false
    },
    inviteGuid: {
      type: String,
      default: '',
      required: false
    },
    currentState: {
      type: Object,
      default: () => { return { fields: [] } },
      required: false
    },
    initState: {
      type: String,
      default: 'init',
      required: false
    },
    ssoToken: {
      type: String,
      default: '',
      required: false
    },
    redirectUrl: {
      type: String,
      default: '',
      required: false
    },
    redirectData: {
      type: String,
      default: '',
      required: false
    },
    sessionGuid: {
      type: String,
      default: '',
      required: false
    },
    consentClientId: {
      type: String,
      default: '',
      required: false
    },
    consentRedirect: {
      type: String,
      default: '',
      required: false
    },
    consentState: {
      type: String,
      default: '',
      required: false
    }
  },

  data () {
    return {
      email: null,
      token: null,
      current_state_definition: this.currentState,
      actions: {},
      state: this.initState,
      titleText: '',
      subtitleText: '',
      values: [],
      error: false,
      errorMessages: [],
      waitingRequest: false,
      waitingRequests: {},
      rules: {
        email: (value) => {
          if (!value) return 'We need your email in order to help you sign in'
          return snValidationsService.email(value) || 'Invalid e-mail'
        },
        phone: (value) => {
          return snValidationsService.phone(value) || 'Invalid phone'
        }
      }
    }
  },

  computed: {
    authParams () {
      return {
        from_web: true,
        auth_context: this.authContext,
        invite_guid: this.inviteGuid,
        sso_token: this.ssoToken
      }
    },
    isMobile () {
      return this.$vuetify.breakpoint.mdAndDown
    },
    isSigningRoom () {
      return this.authContext === 'signingRoom'
    },
    isSkippableAuthContext () {
      return ['signingRoom'].includes(this.authContext)
    },
    cssButtonRow () {
      if (this.$vuetify.breakpoint.mdAndDown) {
        return `
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
        `
      } else {
        return `
          background-color: #F7F7F7;
          display: flex;
          flex-direction: row-reverse;
          justify-content: space-between;
          align-items: center;
          margin: 16px -16px -32px;
          padding: 16px;
          gap: 16px;
        `
      }
    },
    cssSignInButtonRow () {
      return `
        background-color: #F7F7F7;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        margin: 16px -16px -32px;
        padding: 16px;
        gap: 12px;
      `
    },
    loaderMessage () {
      return ['Please wait...']
    },
    isLoading () {
      return this.state === 'init' || this.state === 'requestSsoSignIn' || this.state === 'auth_signed_in' || this.state.includes('auth_sso_sign_in') || this.state.includes('auth_sso_signed_in')
    }
  },

  // Usually the titles for each state are displayed in a parent component so this controls that display
  watch: {
    titleText () {
      this.$emit('title', this.titleText)
    },
    subtitleText () {
      this.$emit('subtitle', this.subtitleText)
    },
    state (newVal) {
      this.$emit('change-state', this.state)
      if (this.state === 'auth_sign_in' && this.$route && this.$route.query.email) {
        this.current_state_definition.fields[0].response = this.$route.query.email
      }
    },
    init (newVal) {
      if (newVal === 'start') {
        this.getStarted(true)
      }
    }
  },

  created () {
    this.getStarted(this.init === 'start', !!this.ssoToken)
    if (this.autoLogin) {
      if (window.PasswordCredential) {
        navigator.credentials.get({
          password: true,
          mediation: 'required'
        }).then(c => {
          if (c) {
            this.current_state_definition.fields[0].response = c.id
            this.current_state_definition.fields[1].response = c.password
            this.nextState()
          }
        }).catch(error => {
          // various browsers throw a NotSupportedError if they don't support credentials
          console.log('credential management is not supported')
          return error
        })
      }
    }
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.confirmLeavePage)
  },

  methods: {
    formatActionLabel (label) {
      if (!label || label !== label.toUpperCase()) return label
      return label.toLowerCase().replace(/(^|\s)\S/g, c => c.toUpperCase())
    },

    keydownEnter () {
      if (!this.waitingRequest) {
        this.nextState()
      }
    },

    handleServicerChoice (actionValue, selectedSac) {
      const action = {
        key: 'event',
        value: actionValue
      }
      if (actionValue === 'save_servicer') {
        this.current_state_definition.fields[0].response = selectedSac
        this.activationCode = selectedSac
        this.$emit('change-activation-code', selectedSac)
        this.nextState(action)
      }
      if (actionValue === 'skip_selection') {
        this.current_state_definition.fields[0].response = this.activationCode
        this.nextState(action)
      }
    },

    createRequest (action) {
      // This function goes through the current state definition and creates a request
      // based on keys and values and the event based on the action passed to this function
      const request = this.authParams
      request.auth_guid = cookie.get('srm_auth_guid')
      if (this.activationCode) { // pass this through as soon as possible otherwise redirect to borrower/signup
        request.activation_code = this.activationCode
      }
      if (this.state.includes('choose_expert')) {
        request.event = action.value
        request.activation_code = this.activationCode
        return request
      }
      if (action) {
        request.event = action.value
        if (this.current_state_definition.fields) {
          for (let x = 0; x < this.current_state_definition.fields.length; x++) {
            request[this.current_state_definition.fields[x].key] = this.current_state_definition.fields[x].response
          }
        }
      }
      return request
    },

    checkErrors (index, checkUndefined) {
      checkUndefined = false
      // Check the rules for different types of fields for validation of input

      // Check to see if we are looking at valid field
      if (this.current_state_definition.fields && this.current_state_definition.fields[index] === undefined) {
        return false
      }

      // Trim input
      if (this.current_state_definition.fields[index].response !== '' && this.current_state_definition.fields[index].response !== undefined) this.current_state_definition.fields[index].response = this.current_state_definition.fields[index].response.trim()

      // Run custom validation
      if (this.rules[this.current_state_definition.fields[index].type]) {
        if (this.rules[this.current_state_definition.fields[index].type](this.current_state_definition.fields[index].response) !== true) {
          this.current_state_definition.fields[index].ruleError = true
          this.current_state_definition.fields[index].error = this.rules[this.current_state_definition.fields[index].type](this.current_state_definition.fields[index].response)
          return true
        }
      }
      return false
    },

    clearErrors (index) {
      this.$set(this.current_state_definition.fields[index], 'ruleError', false)
      if (this.current_state_definition.fields[index].response === undefined) {
        this.current_state_definition.fields[index].response = ''
      }

      let removeErrorMessage = true
      for (let x = 0; x < this.current_state_definition.fields.length; x++) {
        if (this.current_state_definition.fields[x].ruleError === true) {
          removeErrorMessage = false
        } else {
          this.current_state_definition.fields[x].error = ''
        }
      }
      if (removeErrorMessage === true) {
        this.errorMessages = []
        this.error = false
      }
    },

    // Redirect to sessions controller with token
    finishedAuthentication () {
      this.$axios.post('/sessions', {
        token: this.token,
        email: this.email,
        authenticity_token: document.getElementsByName('csrf-token')[0].content,
        fromLogin: true,
        fromMobileFlow: true,
        fromMobileShare: (this.$route?.query?.from_mobile_share === 'true'),
        sac_id: this.sacId,
        user_id: this.userId,
        signer_id: this.signerId,
        session_guid: this.sessionGuid,
        consent_client_id: this.consentClientId,
        consent_redirect: this.consentRedirect,
        consent_state: this.consentState,
        redirect_url: this.redirectUrl,
        redirect_data: this.redirectData
      })
        .then(
          (response) => {
            if (!this.supressRedirects) {
              window.location = response.data
            }
            this.$emit('authenticated')
          }
        )
        .catch(
          (error) => {
            this.$sentry.captureException(error)
            console.log(error)
          }
        )
    },

    ping () {
      const url = '/auth/v1/ping/' + cookie.get('srm_auth_guid')
      this.$axios.get(url)
        .then(
          (response) => {
            if (response.data.current_state !== 'auth_waiting_for_verification') {
              clearInterval(this.pingFunction) // Once we have moved forward in the flow this stops the ping requests from continuing
              window.removeEventListener('beforeunload', this.confirmLeavePage) // remove event listener warning user to not leave page
              if (this.state === 'auth_waiting_for_verification') {
                this.$axios.post('/auth/v1/sync', this.createRequest(null))
                  .then((response) => {
                    this.processAuthResponse(response)
                  })
              }
            }
          }
        )
    },

    nextState (action) {
      // ensure the ping and beforeunload listener are immediately cleared on state transition.
      clearInterval(this.pingFunction)
      window.removeEventListener('beforeunload', this.confirmLeavePage)

      if (action === undefined || action === null || action === '') {
        action = this.current_state_definition.primary_action
      }
      if (this.current_state_definition.fields && this.state !== 'auth_waiting_for_verification' && this.state.includes('choose_expert') && action === this.current_state_definition.primary_action) {
        let errorsExist = false
        for (let x = 0; x < this.current_state_definition.fields.length; x++) {
          // Make sure all the fields have input
          if (!this.current_state_definition.fields[x].response) {
            this.$set(this.current_state_definition.fields[x], 'ruleError', true)
            errorsExist = true
            this.error = true
            this.errorMessages = ['All fields are required']
          } else { // If any fields still have errors don't move to the next state
            errorsExist = this.checkErrors(x, true)
          }
        }
        if (errorsExist) {
          return
        }
      }
      this.setLoader(action, true)
      this.$axios.post('/auth/v1/sync', this.createRequest(action))
        .then(
          (response) => {
            this.setLoader(action, false)
            this.processAuthResponse(response)
          }
        )
    },

    setLoader (action, value) {
      this.waitingRequest = value
      if (action?.value) {
        this.waitingRequests[action.value] = value
      }
    },

    // Currently we are skipping the init state used on mobile
    getStarted (init = false, sso = false) {
      // return
      cookie.set('srm_new_user', true)
      this.$axios.post('/auth/v1/init', this.authParams)
        .then(
          (response) => { // this could be changed if we switch to the full state machine
            // we are using set instead of setting equal to in order to allow vue to responsively react to the changes of the values within the keys.
            // for more info: https://vuejs.org/v2/guide/reactivity.html#Change-Detection-Caveats
            const keys = Object.keys(response.data.global_action_definitions)
            keys.forEach(key => {
              this.$set(this.current_state_definition, key, response.data.global_action_definitions[key])
            })
            cookie.set('srm_auth_guid', response.data.values.auth_guid)
            if (init) {
              this.nextState(response.data.global_action_definitions.primary_action)
            } else if (sso) {
              this.nextState(response.data.global_action_definitions.sso_action)
            } else {
              this.nextState(response.data.global_action_definitions.secondary_action)
            }
          }
        )
    },

    setActions () {
      this.actions = []
      if (this.current_state_definition.primary_action) {
        this.actions = [this.current_state_definition.primary_action] // These are the buttons that are displayed
      }
      if (this.current_state_definition.secondary_action) {
        this.actions = this.actions.concat(this.current_state_definition.secondary_action)
      }
      if (this.current_state_definition.additional_actions) { // If there are additional actions besides main action
        this.actions = this.actions.concat(this.current_state_definition.additional_actions)
      }
    },

    setFields () {
      for (let x = 0; x < this.current_state_definition.fields.length; x++) {
        const field = this.current_state_definition.fields[x]
        field.response = this.values[field.key] // set the response to default values
        // ngc-radio-group has no equivalent of sn-radio-group's `mandatory`, so
        // default radio fields to the first option to preserve that behaviour.
        if (field.options && !field.response) {
          const [firstOption] = this.filteredOptions(field.options)
          if (firstOption) this.$set(field, 'response', firstOption.value)
        }
      }
    },

    setErrors () {
      this.errorMessages = [this.current_state_definition.error]
      if (this.errorMessages && this.errorMessages[0] !== '') this.error = true
      else {
        this.error = false
        this.errorMessages = []
      }
    },

    processAuthResponse (response) {
      // Set current state
      this.state = response.data.current_state
      // the state will be expired if the auth has completed or a time out, so the check for authenticated_data is necessary
      if (this.state === 'init') {
        this.$emit('title', 'With a little information, we\'ll save your work for you.')
        this.getStarted()
        return
      }
      if ((this.state === 'auth_signed_in' || this.state === 'auth_sso_signed_in') && response.data.authenticated_data) {
        const user = response.data.authenticated_data.user
        const signer = response.data.authenticated_data.signer
        this.email = user == null ? '' : user.email
        this.token = response.data.authenticated_data.token
        this.sacId = response.data.authenticated_data.sac_id
        this.userId = user == null ? '' : user.id
        this.signerId = signer == null ? '' : signer.id
        this.finishedAuthentication()
        return
      }

      this.values = response.data.values // gets current preset values such as email, phone name ect. These are displayed as helps to user if fields match
      this.current_state_definition = response.data.current_state_definition || { fields: [] }
      this.setActions()
      this.setFields()
      this.setErrors()
      this.titleText = this.current_state_definition.title
      this.subtitleText = this.current_state_definition.subtitle
      this.fieldHeader = this.current_state_definition.field_header
      // If we are waiting for verification we starting pinging so if they click on the email link we can advance
      if (this.state === 'auth_waiting_for_verification') {
        window.addEventListener('beforeunload', this.confirmLeavePage)
        this.pingFunction = setInterval(function () {
          this.ping()
        }.bind(this), response.data.current_state_definition.refresh_rate)
      }
      // If we are passed the email from another component already (such as borrowerInitial) this will move us forward in the state machine.
      if (this.userEmail && this.state === 'new_input_email' && response.data.success === true) {
        this.current_state_definition.fields[0].response = this.userEmail
        this.state = 'init'
        this.nextState()
      }

      // SSO from monolith to microservice
      if (this.state === 'auth_sso_sign_in' && response.data.success === true) {
        this.nextState(this.current_state_definition.primary_action)
      }
    },

    filteredOptions (options) {
      return options.filter((option) => option.label !== '')
    },

    snRadioOptions (options) {
      return this.filteredOptions(options)?.map(option => {
        return { label: option.label, text: option.label, value: option.value }
      })
    },

    confirmLeavePage (event) {
      // The following set up should work with the following browsers: IE, Edge, Chrome, Safari, Firefox.
      // **NOTE: Only IE allows you to customize the message of the alert. The other
      //         browsers use a standard message notifying the user of unsaved changes.***
      const message = 'Leaving this page now may disrupt the process. Do you still want to leave?'
      event.returnValue = message
      return message
    },

    getCypressTag (type) {
      switch (type) {
        case 'sign_in':
          return 'sign-in-btn'
        case 'save_servicer':
          return 'NEXT-btn'
        case 'I agree-input':
          return 'agree-input'
        case 'complete':
          return 'continue'
        default:
          return type
      }
    }
  }

}
</script>

<style scoped>
  .forgot-password-row {
    width: 100%;
    display: flex;
    justify-content: flex-start;
  }
  .forgot-password-btn::part(button) {
    text-decoration: underline;
    color: #103656;
    font-weight: 600;
    padding: 0;
  }
  .signin-btn::part(button) {
    background-color: #103656;
    color: #FFFFFF;
    padding: 9px 16px;
    border-radius: 2px;
    font-weight: 600;
    font-size: 15px;
    line-height: 22px;
  }
  .full-width-btn {
    display: block;
    width: 100%;
    --ngc-button-width: 100%;
  }
  .full-width-btn::part(button) {
    width: 100%;
  }
  .fixed-width-btn {
    --ngc-button-width: 200px;
    width: 200px;
  }
  .fixed-width-btn::part(button) {
    width: 200px;
  }
</style>
